import { ComponentPropsWithRef, Fragment } from "react";

import { VariantProps, cva } from "class-variance-authority";

import Button from "@/components/Button";
import type { CellAction, CellInfo } from "@/types/table";
import { cn } from "@/utils/cn";

const tableContentCSS = cva("", {
  variants: {
    variant: {},
  },
});

export type SortDirection = "asc" | "desc" | null;
export type SortState = { column: string; direction: SortDirection };

interface TableProps
  extends VariantProps<typeof tableContentCSS>,
    ComponentPropsWithRef<"table"> {
  header: CellInfo[];
  body: CellInfo[][];
  sortableColumns?: string[];
  sortState?: SortState;
  onSort?: (column: string, direction: SortDirection) => void;
}

const SortIcon = ({ direction }: { direction: SortDirection }) => (
  <span className="ml-1 inline-flex flex-col items-center text-[7px] leading-[7px]">
    <span className={direction === "asc" ? "text-primary" : "text-gray-300"}>
      ▲
    </span>
    <span className={direction === "desc" ? "text-primary" : "text-gray-300"}>
      ▼
    </span>
  </span>
);

// 이름이 없는 셀은 액션(버튼) 셀이다. 단일 액션과 복수 액션을 하나의 목록으로 합친다.
const getRowActions = (items: CellInfo[]): CellAction[] =>
  items
    .filter(item => !item.name)
    .flatMap<CellAction>(item =>
      item.actions ? item.actions : [{ data: item.data, method: item.method }],
    )
    .filter(action => Boolean(action.data));

const isEmptyValue = (data: CellInfo["data"]) =>
  data === undefined || data === null || data === "";

interface MobileSortBarProps {
  sortableColumns: string[];
  sortState?: SortState;
  onSort: (column: string, direction: SortDirection) => void;
}

/**
 * 모바일 정렬 바.
 * 카드 목록에는 클릭할 헤더가 없어 정렬 기준과 방향을 따로 고르게 한다.
 */
const MobileSortBar = ({
  sortableColumns,
  sortState,
  onSort,
}: MobileSortBarProps) => {
  const activeColumn = sortState?.direction ? sortState.column : "";
  const direction = sortState?.direction ?? null;

  return (
    <div className="mb-3 flex w-full items-center justify-end gap-2 md:hidden">
      <select
        aria-label="정렬 기준"
        className="h-8 rounded-md border border-gray-200 bg-white px-2 text-xs text-gray-700"
        value={activeColumn}
        onChange={e => {
          const column = e.target.value;
          if (!column) {
            onSort(sortState?.column ?? "", null);
            return;
          }
          onSort(column, direction ?? "asc");
        }}
      >
        <option value="">기본 정렬</option>
        {sortableColumns.map(column => (
          <option key={column} value={column}>
            {column}
          </option>
        ))}
      </select>
      <button
        type="button"
        aria-label={direction === "desc" ? "내림차순" : "오름차순"}
        disabled={!activeColumn}
        className="flex h-8 items-center gap-1 rounded-md border border-gray-200 bg-white px-2 text-xs text-gray-700 disabled:opacity-40"
        onClick={() =>
          onSort(activeColumn, direction === "asc" ? "desc" : "asc")
        }
      >
        {direction === "desc" ? "내림차순" : "오름차순"}
        <SortIcon direction={direction} />
      </button>
    </div>
  );
};

/**
 * 모바일(md 미만) 카드 목록.
 * 표는 고정 % 폭이라 좁은 화면에서 셀이 30~40px 로 줄어 글자가 겹치고
 * 마지막 액션 컬럼(취소/상세 등)이 화면 밖으로 잘린다. 대신 행마다 카드로 펼쳐 보여준다.
 */
const MobileCardList = ({ body }: { body: CellInfo[][] }) => {
  if (body.length === 0) {
    return (
      <div className="w-full rounded-lg border border-gray-200 bg-white py-10 text-center text-sm text-gray-400 md:hidden">
        데이터가 없습니다.
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-3 md:hidden">
      {body.map((items, rowIdx) => {
        const fields = items.filter(item => item.name);
        const actions = getRowActions(items);

        return (
          <div
            key={rowIdx}
            className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
          >
            <dl className="grid grid-cols-[minmax(64px,auto)_1fr] gap-x-3 gap-y-1.5 text-sm">
              {fields.map((item, i) => (
                <Fragment key={i}>
                  <dt className="whitespace-nowrap pt-px text-xs font-medium text-gray-500">
                    {item.name}
                  </dt>
                  <dd className="min-w-0 break-words text-gray-800">
                    {isEmptyValue(item.data) ? "-" : item.data}
                  </dd>
                </Fragment>
              ))}
            </dl>
            {actions.length > 0 && (
              <div className="mt-3 flex flex-wrap justify-end gap-2 border-t border-gray-100 pt-3">
                {actions.map((action, i) => (
                  <Button
                    key={i}
                    variant="outlined"
                    className={cn(
                      "px-3 py-1.5 text-xs font-medium",
                      action.data === "거절" &&
                        "border-gray-300 text-gray-dark hover:bg-gray-100 hover:text-gray-dark",
                    )}
                    onClick={action.method}
                  >
                    {action.data}
                  </Button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const Table = ({
  header,
  body,
  sortableColumns,
  sortState,
  onSort,
}: TableProps) => {
  const handleSort = (columnName: string) => {
    if (!onSort || !columnName) return;
    let next: SortDirection;
    if (sortState?.column !== columnName || sortState?.direction === null) {
      next = "asc";
    } else if (sortState.direction === "asc") {
      next = "desc";
    } else {
      next = null;
    }
    onSort(columnName, next);
  };

  return (
    <>
      {onSort && sortableColumns && sortableColumns.length > 0 && (
        <MobileSortBar
          sortableColumns={sortableColumns}
          sortState={sortState}
          onSort={onSort}
        />
      )}
      <MobileCardList body={body} />
      <div className="hidden w-full overflow-x-auto rounded-lg border border-gray-200 md:block">
        <table className="w-full table-fixed border-collapse">
          <colgroup>
            {header.map((item, i) => (
              <col
                key={i}
                style={item.width ? { width: item.width } : undefined}
              />
            ))}
          </colgroup>
          <thead>
            <tr className="bg-gray-50">
              {header.map((item, i) => {
                const isSortable = sortableColumns?.includes(item.name || "");

                return (
                  <th
                    key={i}
                    className={cn(
                      "whitespace-nowrap border-b border-gray-200 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500",
                      !item.name && "w-auto px-4",
                      isSortable &&
                        "cursor-pointer select-none hover:text-gray-700",
                    )}
                    onClick={
                      isSortable ? () => handleSort(item.name!) : undefined
                    }
                  >
                    <span
                      className={
                        isSortable
                          ? "inline-flex items-center gap-1"
                          : undefined
                      }
                    >
                      {item.name}
                      {isSortable && (
                        <SortIcon
                          direction={
                            sortState?.column === item.name
                              ? sortState?.direction ?? null
                              : null
                          }
                        />
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {body.map((items, rowIdx) => (
              <tr
                key={rowIdx}
                className="transition-all duration-150 hover:-translate-y-px hover:bg-blue-50/40 hover:shadow-md"
              >
                {items.map((item, colIdx) => {
                  if (!item.name) {
                    // Multiple actions in one cell
                    if (item.actions) {
                      return (
                        <td
                          key={colIdx}
                          className="whitespace-nowrap px-4 py-3"
                        >
                          <div className="flex items-center gap-1">
                            {item.actions.map((action, actionIdx) =>
                              action.data ? (
                                <Button
                                  key={actionIdx}
                                  variant="underlined"
                                  className={cn(
                                    "text-nowrap text-xs text-primary",
                                    action.data === "거절" && "text-gray-dark",
                                  )}
                                  onClick={action.method}
                                >
                                  {action.data}
                                </Button>
                              ) : null,
                            )}
                          </div>
                        </td>
                      );
                    }
                    // Single action
                    return (
                      <td key={colIdx} className="whitespace-nowrap px-4 py-3">
                        {item.data && (
                          <Button
                            variant="underlined"
                            className={cn(
                              "text-nowrap text-xs text-primary",
                              item.data === "거절" && "text-gray-dark",
                            )}
                            onClick={item.method}
                          >
                            {item.data}
                          </Button>
                        )}
                      </td>
                    );
                  }
                  return (
                    <td
                      key={colIdx}
                      className="truncate px-4 py-3 text-sm text-gray-700"
                      title={
                        typeof item.data === "string" ? item.data : undefined
                      }
                    >
                      {item.data}
                    </td>
                  );
                })}
              </tr>
            ))}
            {body.length === 0 && (
              <tr>
                <td
                  colSpan={header.length}
                  className="py-16 text-center text-sm text-gray-400"
                >
                  데이터가 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default Table;
